import { CompassIcon } from "@xola/icons";
import clsx from "clsx";
import React from "react";
import { Event, Guide, Permission, Timeslot } from "types";
import { NoTranslate } from "../../../Common/NoTranslate";
import { AssignGuidePopover } from "../../Resources/Guides/AssignGuidePopover";
import { ManageActionButton } from "../ManageActionButton";
import { RosterList, RosterListItem } from "../RosterList";
import { IsGranted } from "@/components/Common/IsGranted";
import { useSeller } from "@/hooks/useAccount";
import { useResource } from "@/hooks/useResource";
import { useVisibility } from "@/hooks/useVisibility";

interface EventGuideListProps {
    event?: Event;
    timeslot?: Timeslot;
    popoverParentId?: string;
    editable?: boolean;
    handleDashboardRoster: (eventId: ID) => void;
}

export const EventGuideList = ({
    event,
    timeslot,
    popoverParentId,
    editable = true,
    handleDashboardRoster,
}: EventGuideListProps) => {
    const [showAssignGuide, { toggle: toggleAssignGuide, hide: hideAssignGuide }] = useVisibility(false);
    const guides = event?.guides ?? [];
    const totalGuideCount = guides.length;
    const hasReservation = event && event.quantity.reserved > 0;
    const hasGuidesAssigned = totalGuideCount > 0;
    const showWarning = hasGuidesAssigned && !hasReservation;

    const handlePopoverClose = (timeslot: Timeslot, newEvent: Event) => {
        handleDashboardRoster(newEvent.id);
    };

    return (
        <div>
            <IsGranted permissions={Permission.RESOURCES_GUIDES_PARTIAL_VIEW}>
                <RosterList
                    heading="Guide"
                    count={totalGuideCount}
                    icon={<CompassIcon />}
                    className="mb-2 max-w-36 hover:cursor-pointer 2xl:max-w-full"
                    warning={
                        showWarning
                            ? "There are no active reservations in this event. You could unassign the guides(s) to make them available to other events"
                            : null
                    }
                    onClick={toggleAssignGuide}
                >
                    {guides.map((guide) => (
                        <GuideItem key={guide.guide.id} guide={guide.guide} status={guide.status} />
                    ))}

                    {!editable && guides.length === 0 && <RosterListItem>No Guides</RosterListItem>}
                </RosterList>
            </IsGranted>

            {editable && (
                <IsGranted permissions={Permission.RESOURCES_GUIDES_ALL_VIEW}>
                    <ManageActionButton text="Manage Guides" onClick={toggleAssignGuide} />
                    <AssignGuidePopover
                        events={event ? [event] : []}
                        timeslots={event ? [] : timeslot ? [timeslot] : []}
                        isVisible={showAssignGuide}
                        target={<span />}
                        popoverParentId={popoverParentId}
                        onClose={hideAssignGuide}
                        onCreatedEvent={handlePopoverClose}
                    />
                </IsGranted>
            )}
        </div>
    );
};

const GuideItem = ({ guide, status }: { guide: Guide; status: string }) => {
    const seller = useSeller();
    const { data: guideData } = useResource<Guide>(["sellers", seller.id, "guides", guide.id]);
    return (
        <RosterListItem className={clsx(status === "pending" ? "text-danger" : "text-success")}>
            <NoTranslate>{guideData?.name}</NoTranslate>
        </RosterListItem>
    );
};
